import { useCallback, useEffect, useRef, useState } from "react"
import { websocketUrl } from "../api/client"

// These types are duplicated (not imported) from worker/src/websocket/protocol.ts
// because the web app and worker are separate TypeScript projects/builds.
// Keep them in sync; consider extracting a shared `packages/protocol` module
// if you introduce a monorepo build tool with workspace type sharing.
export type ServerMessage = Record<string, unknown> & { type: string }
export type ConnectionStatus = "connecting" | "connected" | "reconnecting" | "disconnected"

function uuid(): string {
	return crypto.randomUUID()
}

export function useGameSocket(roomCode: string | null) {
	const [status, setStatus] = useState<ConnectionStatus>("connecting")
	const [lastMessage, setLastMessage] = useState<ServerMessage | null>(null)
	const socketRef = useRef<WebSocket | null>(null)
	const reconnectAttempt = useRef(0)

	const connect = useCallback(() => {
		if (!roomCode) return
		const ws = new WebSocket(websocketUrl(roomCode))
		socketRef.current = ws
		setStatus("connecting")

		ws.addEventListener("open", () => {
			reconnectAttempt.current = 0
			setStatus("connected")
		})
		ws.addEventListener("message", (event) => {
			try {
				setLastMessage(JSON.parse(event.data))
			} catch {
				// ignore malformed frame
			}
		})
		ws.addEventListener("close", () => {
			setStatus("reconnecting")
			const delay = Math.min(1000 * 2 ** reconnectAttempt.current, 10_000)
			reconnectAttempt.current += 1
			setTimeout(connect, delay)
		})
		ws.addEventListener("error", () => {
			setStatus("disconnected")
		})
	}, [roomCode])

	useEffect(() => {
		connect()
		return () => {
			socketRef.current?.close()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [roomCode])

	const send = useCallback((message: Record<string, unknown>) => {
		if (socketRef.current?.readyState === WebSocket.OPEN) {
			socketRef.current.send(JSON.stringify(message))
		}
	}, [])

	const rollDice = useCallback(() => send({ type: "ROLL_DICE", requestId: uuid() }), [send])
	const movePiece = useCallback((pieceId: string) => send({ type: "MOVE_PIECE", requestId: uuid(), pieceId }), [send])
	const leaveRoom = useCallback(() => send({ type: "LEAVE_ROOM", requestId: uuid() }), [send])

	return { status, lastMessage, rollDice, movePiece, leaveRoom }
}
